// /admin/usuarios-admin/invitaciones — lista invitaciones admin (super-admin only).
//
// Filtra por status (pendiente/usada/expirada/revocada). Acción "revocar" para
// invitaciones pendientes. Sin botón "crear" — eso vive en /admin/usuarios-admin
// (botón principal "Invitar nuevo admin").

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, AlertCircle, CheckCircle2, Loader2, X, Clock } from 'lucide-react';

import { listInvitacionesAdmin, revocarInvitacion } from '../../lib/adminApi';
import { ApiError } from '../../lib/api';

function localePara(lang) {
  const map = { es: 'es-ES', en: 'en-GB', ru: 'ru-RU', fr: 'fr-FR' };
  return map[lang] || 'es-ES';
}

function fmtFechaHora(iso, lang) {
  if (!iso) return '—';
  const d = new Date(iso);
  return new Intl.DateTimeFormat(localePara(lang), {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(d);
}

const STATUS_FILTROS = ['', 'pendiente', 'usada', 'expirada', 'revocada'];

export default function AdminUsuariosAdminInvitaciones() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [banner, setBanner] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await listInvitacionesAdmin(filtro ? { status: filtro } : {});
      setData(d);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [filtro]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (!banner) return;
    const id = setTimeout(() => setBanner(null), 4500);
    return () => clearTimeout(id);
  }, [banner]);

  const handleRevocar = async (inv) => {
    if (!window.confirm(t('admin.invitaciones.confirmar_revocar', { email: inv.email }))) return;
    try {
      await revocarInvitacion(inv.id);
      setBanner({ tipo: 'ok', texto: t('admin.invitaciones.revocada_ok') });
      cargar();
    } catch (e) {
      const msg = e instanceof ApiError
        ? (t('admin.invitaciones.errores.' + e.code, { defaultValue: '' }) || e.message)
        : t('auth.errores.generico');
      setBanner({ tipo: 'err', texto: msg });
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <Link to="/admin/usuarios-admin" className="btn-link-back">
            <ArrowLeft size={14} /> {t('admin.invitaciones.volver')}
          </Link>
          <h1 className="admin-page-titulo">
            <Mail size={22} strokeWidth={1.5} /> {t('admin.invitaciones.titulo')}
          </h1>
          <p className="admin-page-sub">{t('admin.invitaciones.sub')}</p>
        </div>
        <div className="admin-filtros-inline">
          <label className="admin-label">
            {t('admin.invitaciones.filtro_status')}{' '}
            <select value={filtro} onChange={(e) => setFiltro(e.target.value)}>
              {STATUS_FILTROS.map((s) => (
                <option key={s} value={s}>{s ? t('admin.invitaciones.status.' + s) : t('admin.invitaciones.todos')}</option>
              ))}
            </select>
          </label>
        </div>
      </header>

      {banner && (
        <div className={banner.tipo === 'ok' ? 'admin-msg-ok' : 'modal-error-general'} style={{ marginBottom: 16 }}>
          {banner.tipo === 'ok' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          <span>{banner.texto}</span>
        </div>
      )}

      {loading && (
        <div className="admin-loading"><Loader2 size={20} className="spin" /> <span>{t('admin.cargando')}</span></div>
      )}
      {error && (
        <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>
      )}

      {!loading && !error && data && data.data.length === 0 && (
        <div className="admin-empty">{t('admin.invitaciones.vacio')}</div>
      )}

      {!loading && !error && data && data.data.length > 0 && (
        <div className="admin-card">
          <table className="admin-tabla">
            <thead>
              <tr>
                <th>{t('admin.invitaciones.col_email')}</th>
                <th>{t('admin.invitaciones.col_status')}</th>
                <th>{t('admin.invitaciones.col_creada')}</th>
                <th>{t('admin.invitaciones.col_expira')}</th>
                <th>{t('admin.invitaciones.col_usada')}</th>
                <th className="admin-col-acciones">{t('admin.acciones')}</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.email}</td>
                  <td>
                    <span className={`admin-pill admin-pill--${inv.status === 'pendiente' ? 'sage' : inv.status === 'usada' ? 'neutro' : 'clay'}`}>
                      {t('admin.invitaciones.status.' + inv.status)}
                    </span>
                  </td>
                  <td className="admin-celda-tenue">{fmtFechaHora(inv.created_at, lang)}</td>
                  <td className="admin-celda-tenue">
                    <Clock size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    {fmtFechaHora(inv.expires_at, lang)}
                  </td>
                  <td className="admin-celda-tenue">{inv.used_at ? fmtFechaHora(inv.used_at, lang) : '—'}</td>
                  <td className="admin-col-acciones">
                    {inv.status === 'pendiente' ? (
                      <button type="button" className="btn-icon" title={t('admin.invitaciones.tip_revocar')} onClick={() => handleRevocar(inv)}>
                        <X size={14} />
                      </button>
                    ) : (
                      <span className="admin-celda-tenue">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
